import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, LogOut, ShieldCheck, Sun, Moon, Monitor, Images, CalendarCog, Sparkles, BarChart3, TrendingUp, ExternalLink, ArrowLeftRight, Clock } from 'lucide-react';
import { clayTapScale } from '../../constants/animations';
import { getMachhaAsset, getMachhaAssetSrcSet, MACHHA_FALLBACK_SRC } from '../../lib/machhaAsset';
import { Avatar } from './Avatar';
import type { User, QuotaUsage } from '../../types';
import type { ThemeValue } from './ThemeToggleUI';
import styles from './Navbar.module.css';

const themes = [
  { value: 'light'  as const, icon: Sun,     label: 'Light'  },
  { value: 'dark'   as const, icon: Moon,    label: 'Dark'   },
  { value: 'system' as const, icon: Monitor, label: 'System' },
];

interface NavbarUIProps {
  user: User | null;
  pathname: string;
  theme: ThemeValue;
  logoLoaded: boolean;
  dropdownOpen: boolean;
  hidden?: boolean;
  quotaUsage?: QuotaUsage | null;
  /** Slot rendered just before the user avatar — currently used for the notification bell. */
  notificationsSlot?: React.ReactNode;
  portal?: 'creator' | 'viewer';
  onLogoLoad: () => void;
  onToggleDropdown: () => void;
  onCloseDropdown: () => void;
  onOpenSettings: () => void;
  onOpenQuota?: () => void;
  onLogout: () => void;
  onThemeChange: (value: ThemeValue) => void;
  onLegalOpen: (type: 'terms' | 'privacy') => void;
  onCreatorModeAction?: () => void;
  onNavigateAdmin?: () => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
}

export function NavbarUI({
  user,
  pathname,
  theme,
  logoLoaded,
  dropdownOpen,
  hidden = false,
  quotaUsage,
  notificationsSlot,
  portal,
  onLogoLoad,
  onToggleDropdown,
  onCloseDropdown,
  onOpenSettings,
  onOpenQuota,
  onLogout,
  onThemeChange,
  onLegalOpen,
  onCreatorModeAction,
  onNavigateAdmin,
  dropdownRef,
}: NavbarUIProps) {
  const eventsActive = pathname.startsWith('/events');
  const albumsActive = pathname === '/' || pathname.startsWith('/albums');
  const insightsActive = pathname.startsWith('/insights');
  const showEventsLink = portal === 'creator' || (!portal && (user?.betaApproved || user?.isOwner));
  const showAlbumsLink = !portal;
  const showInsightsLink = portal === 'creator';
  const showQuotaBlock = portal !== 'viewer';
  const logoHref = portal === 'creator' ? '/events' : '/albums';

  // Portal-aware creator mode item
  type CreatorModeVariant = {
    label: string;
    icon: React.ElementType;
    disabled?: boolean;
  } | null;
  const creatorModeItem: CreatorModeVariant = (() => {
    if (!user || !onCreatorModeAction) return null;
    if (portal === 'creator') return { label: 'Switch to Viewer', icon: ArrowLeftRight };
    if (user.betaApproved || user.isOwner) return { label: 'Open Creator Mode', icon: ExternalLink };
    if (user.waitlistStatus === 'pending') return { label: 'Application Pending', icon: Clock, disabled: true };
    return { label: 'Become a Creator', icon: Sparkles };
  })();

  return (
    <motion.nav
      className={styles.nav}
      initial={{ y: -72, opacity: 0 }}
      animate={{ y: hidden ? -72 : 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
    >
      <div className={styles.inner}>
        <div className={styles.bar}>
          {/* Left: Logo + Nav links */}
          <div className={styles.left}>
            <Link to={logoHref} className={styles['logo-link']}>
              <motion.img
                src={getMachhaAsset('LogoImages', 'pixelmachha_icon_logo', 1)}
                srcSet={getMachhaAssetSrcSet('LogoImages', 'pixelmachha_icon_logo')}
                alt="Pixel Machha logo"
                className={styles['logo-img']}
                style={{ opacity: logoLoaded ? 1 : 0 }}
                onLoad={onLogoLoad}
                onError={(e) => {
                  const t = e.currentTarget;
                  if (!t.dataset.fallback) {
                    t.dataset.fallback = 'true';
                    t.src = MACHHA_FALLBACK_SRC;
                    t.srcset = '';
                  }
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={clayTapScale}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
              />
              <motion.span
                className={styles['logo-wordmark']}
                whileHover={{ x: 2 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                Pixel Machha
              </motion.span>
            </Link>

            {user && (
              <>
                {/* Albums link */}
                {showAlbumsLink && (
                <Link
                  to="/albums"
                  className={[styles['nav-link'], styles['nav-link-with-icon'], albumsActive ? styles['nav-link-active'] : ''].filter(Boolean).join(' ')}
                >
                  {albumsActive && (
                    <motion.span layoutId="nav-pill" className={styles['nav-pill']} transition={{ type: 'spring', stiffness: 400, damping: 28 }} />
                  )}
                  <Images className={styles['nav-icon']} />
                  <span className={albumsActive ? styles['nav-label-mobile'] : styles['nav-label-desktop']}>Albums</span>
                </Link>
                )}

                {/* Events link — beta-approved users only */}
                {showEventsLink && (
                  <Link
                    to="/events"
                    className={[styles['nav-link'], styles['nav-link-with-icon'], eventsActive ? styles['nav-link-active'] : ''].filter(Boolean).join(' ')}
                  >
                    {eventsActive && (
                      <motion.span layoutId="nav-pill" className={styles['nav-pill']} transition={{ type: 'spring', stiffness: 400, damping: 28 }} />
                    )}
                    <CalendarCog className={styles['nav-icon']} />
                    <span className={eventsActive ? styles['nav-label-mobile'] : styles['nav-label-desktop']}>Events</span>
                  </Link>
                )}

                {/* Insights link — creator portal only */}
                {showInsightsLink && (
                  <Link
                    to="/insights"
                    className={[styles['nav-link'], styles['nav-link-with-icon'], insightsActive ? styles['nav-link-active'] : ''].filter(Boolean).join(' ')}
                  >
                    {insightsActive && (
                      <motion.span layoutId="nav-pill" className={styles['nav-pill']} transition={{ type: 'spring', stiffness: 400, damping: 28 }} />
                    )}
                    <TrendingUp className={styles['nav-icon']} />
                    <span className={insightsActive ? styles['nav-label-mobile'] : styles['nav-label-desktop']}>Insights</span>
                  </Link>
                )}

              </>
            )}
          </div>

          <div className={styles.right}>
            {notificationsSlot}
            {/* Avatar + dropdown */}
            {user && (
              <div className={styles['dropdown-wrap']} ref={dropdownRef}>
                <motion.button
                  className={styles['avatar-btn']}
                  onClick={onToggleDropdown}
                  whileHover={{ scale: 1.05 }}
                  whileTap={clayTapScale}
                  transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                >
                  <Avatar src={user.picture} name={user.name} size="md" />
                </motion.button>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      className={styles.dropdown}
                      initial={{ opacity: 0, scale: 0.95, y: -8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -8 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    >
                      {/* ── User identity header ── */}
                      <div className={styles['dropdown-header']}>
                        <Avatar src={user.picture} name={user.name} size="md" />
                        <div className={styles['dropdown-header-info']}>
                          <span className={styles['dropdown-header-name']}>{user.name}</span>
                          <span className={styles['dropdown-header-email']}>{user.email}</span>
                        </div>
                      </div>

                      <hr className={styles.divider} />

                      {/* ── Theme picker ── */}
                      <div className={styles['dropdown-theme-row']}>
                        <div className={styles['dropdown-theme-inner']} role="radiogroup" aria-label="Theme preference">
                          {themes.map(({ value, icon: Icon, label }) => {
                            const isActive = theme === value;
                            return (
                              <button
                                key={value}
                                role="radio"
                                aria-checked={isActive}
                                aria-label={label}
                                className={[
                                  styles['dropdown-theme-btn'],
                                  isActive ? styles['dropdown-theme-btn-active'] : styles['dropdown-theme-btn-inactive'],
                                ].join(' ')}
                                onClick={() => onThemeChange(value)}
                              >
                                {isActive && (
                                  <motion.span
                                    layoutId="dropdown-theme-pill"
                                    className={styles['dropdown-theme-pill']}
                                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                                  />
                                )}
                                <Icon style={{ position: 'relative', width: 16, height: 16 }} />
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── Creator mode action ── */}
                      {creatorModeItem && (
                        <div className={styles['dropdown-actions']}>
                          <motion.button
                            className={[
                              styles['dropdown-item-beta'],
                              creatorModeItem.disabled ? styles['dropdown-item-beta-disabled'] : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => { if (!creatorModeItem.disabled) { onCloseDropdown(); onCreatorModeAction?.(); } }}
                            whileHover={creatorModeItem.disabled ? undefined : { x: 2 }}
                            whileTap={creatorModeItem.disabled ? undefined : { scale: 0.98 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            disabled={creatorModeItem.disabled}
                          >
                            <creatorModeItem.icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                            {creatorModeItem.label}
                          </motion.button>
                        </div>
                      )}

                      {/* ── Quota summary with progress bar ── */}
                      {showQuotaBlock && quotaUsage && user && user.betaApproved && !user.isOwner && (
                        <motion.button
                          className={styles['dropdown-quota']}
                          onClick={onOpenQuota}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                          <div className={styles['dropdown-quota-top']}>
                            <div className={styles['dropdown-quota-label']}>
                              <BarChart3 style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.6 }} />
                              <span>Events</span>
                            </div>
                            <span className={styles['dropdown-quota-count']}>
                              {quotaUsage.events.used}
                              <span className={styles['dropdown-quota-limit']}>/{quotaUsage.events.limit}</span>
                            </span>
                          </div>
                          <div className={styles['dropdown-quota-bar']}>
                            {quotaUsage.events.used > 0 && (
                              <motion.div
                                className={styles['dropdown-quota-fill']}
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min((quotaUsage.events.used / quotaUsage.events.limit) * 100, 100)}%` }}
                                transition={{ type: 'spring', stiffness: 200, damping: 25, delay: 0.15 }}
                              />
                            )}
                          </div>
                        </motion.button>
                      )}

                      <hr className={styles.divider} />

                      {/* ── Actions ── */}
                      <div className={styles['dropdown-actions']}>
                        <button
                          className={styles['dropdown-item']}
                          onClick={() => { onCloseDropdown(); onOpenSettings(); }}
                        >
                          <Settings style={{ width: 16, height: 16, flexShrink: 0 }} />
                          Settings
                        </button>
                        {user.isOwner && onNavigateAdmin && (
                          <button
                            className={styles['dropdown-item']}
                            onClick={() => { onCloseDropdown(); onNavigateAdmin(); }}
                          >
                            <ShieldCheck style={{ width: 16, height: 16, flexShrink: 0 }} />
                            Admin
                          </button>
                        )}
                        <button
                          className={[styles['dropdown-item'], styles['dropdown-item-danger']].join(' ')}
                          onClick={() => { onCloseDropdown(); onLogout(); }}
                        >
                          <LogOut style={{ width: 16, height: 16, flexShrink: 0 }} />
                          Sign out
                        </button>
                      </div>

                      {/* ── Legal ── */}
                      <div className={styles['dropdown-legal']}>
                        <button className={styles['dropdown-legal-link']} onClick={() => onLegalOpen('privacy')}>Privacy</button>
                        <span className={styles['dropdown-legal-sep']}>·</span>
                        <button className={styles['dropdown-legal-link']} onClick={() => onLegalOpen('terms')}>Terms</button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
}

